import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { ModalProps } from 'semantic-ui-react';
import { useMutation } from '@apollo/client';
import {
  IInspectSubjectMutation,
  IInspectSubjectMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import { INSPECT_SUBJECT } from '../../../utils/apollo/cohorts';
import { useChartContext } from '../ChartContext';
import {
  FullScreenModal,
  FullScreenModalCloseAction,
  FullScreenModalContent,
} from '../../FullscreenContainer/FullscreenContainer';
import { setInspectSubjectIdAction } from '../../../redux/slices/analyses';
import { useAppDispatch } from '../../../redux/hooks';
import { CohortSampleSummaryDisplay } from '../CohortSampleChart';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { ContentLoader } from '../../Loaders';
import UserInspectViewTable from './UserInspectViewTable';
import { useInspectReportSettingFieldKeys } from './utils';
import DatePickerDisplay from '../../Builder/DatePickerWidget/DateRangeDisplay/DatePickerDisplay';
import { datePickerFromAnalysisInfo } from '../../Builder/DatePickerWidget/utils';
import DateDisplay from '../../Builder/DatePickerWidget/DateRangeDisplay/DateDisplay';
import { theme } from '../../../styles/theme';
import ThinDivider from '../../ThinDivider';
import ErrorChartInfo from '../ErrorChartInfo';
import { ChartViewType } from '../types';

const Header = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
`;

const StyledLabel = styled.div`
  font-size: 12px;
  font-weight: ${theme.colors.secondary800};
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
`;

const ModalLoader = styled(ContentLoader)`
  min-height: 100%;
`;

interface IProps {
  analysisId: string;
  schemaId: string;
}

const middleDot = <span>&#183;</span>;

/**
 * Displaying Inspect Table with Modal
 */
export default ({ analysisId, schemaId }: IProps) => {
  const dispatch = useAppDispatch();
  const ref = useRef<ModalProps['ref']>(null);

  const reportSettingFieldKeys = useInspectReportSettingFieldKeys(schemaId);

  const handleMutationComplete = ({
    inspectSubject,
  }: IInspectSubjectMutation) => {
    if (printGraphqlErrors(inspectSubject?.errors)) {
      return;
    }
  };
  const [inspectSubject, { data, loading }] = useMutation<
    IInspectSubjectMutation,
    IInspectSubjectMutationVariables
  >(INSPECT_SUBJECT, {
    onCompleted: handleMutationComplete,
  });

  const inspectTable = data?.inspectSubject?.data?.inspectTable;
  const exportUrl = data?.inspectSubject?.exportUrl ?? null;
  const error = data?.inspectSubject?.error;

  const {
    settings: { inspectSubjectId },
    metadata: { startDate, endDate, query, datePicker },
  } = useChartContext();
  assertNotNil(inspectSubjectId);
  assertCondition(query?.__typename === 'CohortSampleQuery');
  const { cohort } = query;

  // XXX compound doesn't have subject - we use the first simple cohprt subject here
  const subject =
    'subject' in cohort ? cohort.subject : cohort.simpleCohorts[0].subject;
  assertNotNil(subject);

  const clearInspectSubjectId = useCallback(() => {
    dispatch(setInspectSubjectIdAction({ analysisId, subjectId: '' }));
  }, [analysisId, dispatch]);

  const handleInspectMutation = useCallback(async () => {
    if (!schemaId) {
      return;
    }
    await inspectSubject({
      variables: {
        input: {
          start: startDate,
          end: endDate,
          subject: subject.key,
          schemaId,
          subjectId: inspectSubjectId,
          fieldKeys: reportSettingFieldKeys,
        },
      },
    });
  }, [
    endDate,
    inspectSubject,
    inspectSubjectId,
    reportSettingFieldKeys,
    schemaId,
    startDate,
    subject,
  ]);

  const clientHeight = ref?.current?.ref?.current?.clientHeight;
  const presetTableHeight = useMemo(() => {
    if (!clientHeight) {
      return;
    }

    // reduce the height of rest elements in this component
    return clientHeight - 230;
  }, [clientHeight]);

  const handleInspectWithFieldKeys = (fieldKeys: string[]) => {
    inspectSubject({
      variables: {
        input: {
          start: startDate,
          end: endDate,
          subject: subject.key,
          schemaId,
          subjectId: inspectSubjectId,
          fieldKeys,
        },
      },
    });
  };

  useEffect(() => {
    // add event listener for ESC to close the modal
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearInspectSubjectId();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [clearInspectSubjectId]);

  /**
   * trigger mutation once we have inspect subject Id
   */
  useEffect(() => {
    handleInspectMutation();
  }, [handleInspectMutation]);

  return (
    <FullScreenModal open={true} ref={ref}>
      <FullScreenModalCloseAction onClick={clearInspectSubjectId} />
      <FullScreenModalContent id={'InspectViewModalContent'}>
        {loading ? (
          <ModalLoader noBorders={true} />
        ) : (
          <Content>
            <Header>{`Inspect: ${inspectSubjectId}`}</Header>
            <ThinDivider layout="horizontal" size="full" />
            <TitleRow>
              <StyledLabel>
                {`${subject.name} activity feed `}
                {middleDot}{' '}
                <DatePickerDisplay
                  datePicker={datePickerFromAnalysisInfo({
                    start: startDate,
                    end: endDate,
                    datePicker,
                  })}
                  hideFixedDateRange={true}
                />{' '}
                {middleDot} <DateDisplay date={startDate} /> -{' '}
                <DateDisplay date={endDate} />
              </StyledLabel>
              {inspectTable?.summary && (
                <CohortSampleSummaryDisplay
                  tableSize={inspectTable.summary.tableSize}
                  totalCount={inspectTable.summary.totalCount}
                />
              )}
            </TitleRow>
            {inspectTable ? (
              <UserInspectViewTable
                columns={inspectTable.columns}
                subject={subject}
                exportUrl={exportUrl}
                presetTableHeight={presetTableHeight}
                schemaId={schemaId}
                onSubmitFields={handleInspectWithFieldKeys}
              />
            ) : error ? (
              <ErrorChartInfo
                type={ChartViewType.KobolError}
                analysisId={analysisId}
                errorStatus={error.status}
                failureMsg={error.message}
              />
            ) : (
              <ErrorChartInfo type={ChartViewType.FallbackError} />
            )}
          </Content>
        )}
      </FullScreenModalContent>
    </FullScreenModal>
  );
};
